import { NavCustomizationSetting } from '@experiences/constants';
import { UiProgressButton } from '@experiences/ui-common';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import React, {
    useCallback,
    useEffect,
} from 'react';
import {
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import { notificationType } from '../../../../common/constants/Constant';
import useCheckLicense from '../../../../common/hooks/useCheckLicense';
import { useIsAdminRevampEnabled } from '../../../../common/hooks/useIsAdminRevampEnabled';
import type { ISetting } from '../../../../services/identity/SettingService';
import {
    getSetting,
    saveSetting,
    settingUrl,
} from '../../../../services/identity/SettingService';
import { accountGlobalId } from '../../../../store/selectors';
import {
    mapNavCustomizationDataToKeyValuePairs,
    mapSettingArrayToNavCustomizationData,
} from '../../../../util/setting/NavCustomizationSettingUtil';
import UiForm from '../../../common/UiForm';
import NavCustomizationForm from './NavCustomizationForm';

const useStyles = makeStyles(() =>
    createStyles({
        actions: {
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        cancelButton: { marginLeft: '10px' },
        form: {
            width: '518px',
            marginBottom: '16px',
        },
    }),
);

export interface INavCustomizationData {
    appsHidden: boolean;
    marketplaceHidden: boolean;
    resourceCenterHidden: boolean;
    studioHidden?: boolean;
}

export const defaultNavCustomizationData: INavCustomizationData = {
    appsHidden: false,
    marketplaceHidden: false,
    resourceCenterHidden: false,
    studioHidden: false,
};

export const defaultEnterpriseNavCustomizationData: INavCustomizationData = {
    ...defaultNavCustomizationData,
    marketplaceHidden: true,
};

export const navCustomizationKeys = [
    NavCustomizationSetting.AppsHidden,
    NavCustomizationSetting.MarketplaceHidden,
    NavCustomizationSetting.ResourceCenterHidden,
    NavCustomizationSetting.StudioWebHidden,
];

const NavCustomizationComponent: FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { enqueueSnackbar } = useSnackbar();

    const { isUserEnterprise } = useCheckLicense();
    const isAdminRevampEnabled = useIsAdminRevampEnabled();

    const defaultFormValues = isUserEnterprise
        ? defaultEnterpriseNavCustomizationData
        : defaultNavCustomizationData;

    const methods = useForm<INavCustomizationData>({
        mode: 'onSubmit',
        defaultValues: defaultFormValues,
    });

    const {
        reset, handleSubmit, formState,
    } = methods;
    const {
        isDirty, isSubmitting,
    } = formState;

    const partitionGlobalId = useSelector(accountGlobalId);
    const {
        data: fetchedSettings, mutate,
    } = useSWR<ISetting[], Error>(
        [ settingUrl, navCustomizationKeys, partitionGlobalId ],
        getSetting,
        { shouldRetryOnError: false },
    );

    const handleCancel = useCallback(() => {
        reset();
    }, [ reset ]);

    const createNotification = useCallback(
        (message: string, type = notificationType.SUCCESS) => {
            enqueueSnackbar(message, { variant: type as any });
        },
        [ enqueueSnackbar ],
    );

    const onSubmit = useCallback(
        async (data: INavCustomizationData) => {
            try {
                const savedData = await saveSetting(settingUrl, {
                    settings: mapNavCustomizationDataToKeyValuePairs(data),
                    partitionGlobalId,
                });
                mutate(savedData);
                createNotification(translate({ id: 'CLIENT_SETTINGS_UPDATED' }));
            } catch (error) {
                createNotification(translate({ id: 'CLIENT_SETTINGS_UPDATE_ERROR' }), notificationType.ERROR);
            }
        },
        [ createNotification, mutate, partitionGlobalId, translate ],
    );

    useEffect(() => {
        if (fetchedSettings) {
            reset(mapSettingArrayToNavCustomizationData(fetchedSettings, defaultFormValues));
        }
    }, [ reset, fetchedSettings, defaultFormValues ]);

    return !isAdminRevampEnabled
        ? (
            <UiForm
                className={classes.form}
                onSubmit={handleSubmit(onSubmit)}
                actions={
                    <div className={classes.actions}>
                        <UiProgressButton
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isDirty}
                            variant="contained"
                            data-cy="nav-customization-submit-button"
                        >
                            {translate({ id: 'CLIENT_SAVE' })}
                        </UiProgressButton>
                        {isDirty && (
                            <Button
                                className={classes.cancelButton}
                                onClick={() => {
                                    handleCancel();
                                }}
                                color="primary"
                                data-cy="nav-customization-cancel-button"
                            >
                                {translate({ id: 'CLIENT_CANCEL' })}
                            </Button>
                        )}
                    </div>
                }
                isDrawer
            >
                <FormProvider {...methods}>
                    <NavCustomizationForm />
                </FormProvider>
            </UiForm>
        )
        : (
            <NavCustomizationForm />
        )
    ;
};

export default NavCustomizationComponent;
